import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import api, { setToken, clearToken } from '@/utils/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=') || window.location.pathname === '/auth/callback') {
      setLoading(false);
      return;
    }
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
    } catch {
      setUser(null);
      clearToken();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    if (res.data.token) setToken(res.data.token);
    setUser(res.data);
    return res.data;
  };

  const register = async (data) => {
    const res = await api.post('/auth/register', data);
    if (res.data.token) setToken(res.data.token);
    setUser(res.data);
    return res.data;
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch (_) {}
    clearToken();
    setUser(null);
  };

  const processGoogleAuth = async (sessionId) => {
    const res = await api.get('/auth/session', {
      headers: { 'X-Session-ID': sessionId }
    });
    if (res.data.token) setToken(res.data.token);
    setUser(res.data);
    return res.data;
  };

  const updateRole = async (role) => {
    await api.put('/auth/role', { role });
    setUser(prev => ({ ...prev, role }));
  };

  const switchRole = async (role) => {
    try {
      const res = await api.put('/auth/switch-role', { role });
      setUser(res.data);
    } catch (err) {
      console.error('Switch role failed:', err);
    }
  };

  const refreshUser = async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
    } catch (_) {}
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, processGoogleAuth, updateRole, switchRole, refreshUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
